'use client'
import { motion } from 'motion/react'
import Link from 'next/link'
import type { FC } from 'react'
import { IconComponent } from '@/components/Icon/Icon'
import type { SliceComponentProps, SliceLike } from '@/lib/rich-text'

type Button = { text: string; href: string; variant: 'Contained' | 'Text'; target?: '_blank' }

/**
 * A row-labelled head-to-head table, for landing pages aimed at people already
 * running a player-tied CMS. Nothing else in `slices/` renders a comparison
 * with row labels — the card grids all lose the point, which is that the same
 * question gets two different answers.
 *
 * Name no competitor trademarks in `columns` or `rows`: say "a player-tied
 * CMS". A named comparison is a factual claims matrix that has to be kept true
 * and defended; the product names belong in FAQ prose, where they read as
 * informational.
 */
export interface ComparisonSlice extends SliceLike<'comparison'> {
  variation: 'default'
  primary: {
    eyebrow?: string
    title: string
    text?: string
    /** Column headers, in order: the incumbent, then Signital. */
    columns: [string, string]
    rows: { label: string; a: string; b: string }[]
    footnote?: string
    buttons?: Button[]
  }
}

export type ComparisonProps = SliceComponentProps<ComparisonSlice>

const TEXT_LINK_CLASS =
  'hover:text-primary inline-flex items-center justify-center gap-2 text-[16px] font-semibold text-[#2d033b] transition'

const TextLink: FC<{ button: Button }> = ({ button }) => {
  const chevron = <IconComponent icon="ChevronRight" size="xsmall" className="text-primary" />
  return button.href.startsWith('http') ? (
    <a
      href={button.href}
      target={button.target}
      rel={button.target === '_blank' ? 'noopener noreferrer' : undefined}
      className={TEXT_LINK_CLASS}
    >
      {chevron}
      {button.text}
    </a>
  ) : (
    <Link href={button.href} className={TEXT_LINK_CLASS}>
      {chevron}
      {button.text}
    </Link>
  )
}

const Comparison: FC<ComparisonProps> = ({ slice }) => {
  const { eyebrow, title, text, columns, rows, footnote, buttons } = slice.primary
  const [incumbent, ours] = columns

  return (
    <section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      className="bg-white py-20 md:py-28"
    >
      <div className="container">
        <div className="max-w-[720px]">
          {eyebrow && <p className="eyebrow">{eyebrow}</p>}
          <h2 className="display-title mt-3">{title}</h2>
          {text && <p className="mt-5 text-[17px] leading-7 text-[#4b4b4b]">{text}</p>}
        </div>

        {/* Below md the table becomes a stacked card per row. A horizontally
            scrolling table put the Signital column off-screen, which is the
            whole point of the comparison — and mobile is most of the paid
            traffic. Only one of the two trees is ever in the a11y tree. */}
        <motion.ul
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.1 }}
          transition={{ duration: 0.5, ease: 'easeOut' }}
          className="mt-10 space-y-4 md:hidden"
        >
          {rows.map((row) => (
            <li key={row.label} className="rounded-2xl ring-1 ring-black/10">
              <p className="text-ink px-5 pt-5 text-[15px] leading-6 font-semibold">{row.label}</p>
              <div className="px-5 pt-3 pb-4">
                <p className="text-[12px] font-semibold tracking-[0.12em] text-[#9a9a9a] uppercase">
                  {incumbent}
                </p>
                <p className="mt-1 text-[15px] leading-6 text-[#6b6b6b]">{row.a}</p>
              </div>
              <div className="bg-light-gray rounded-b-2xl px-5 py-4">
                <p className="text-[12px] font-semibold tracking-[0.12em] text-[#6b6b6b] uppercase">{ours}</p>
                <p className="mt-1 flex items-start gap-2.5 text-[15px] leading-6 text-[#2f2f2f]">
                  <span className="bg-primary/10 text-primary mt-0.5 flex size-5 shrink-0 items-center justify-center rounded-full">
                    <IconComponent icon="CheckMark" size="xsmall" className="size-3" />
                  </span>
                  {row.b}
                </p>
              </div>
            </li>
          ))}
        </motion.ul>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.5, ease: 'easeOut' }}
          className="mt-12 hidden md:block"
        >
          <table className="w-full border-collapse text-left">
            <caption className="sr-only">
              {title}: {incumbent} compared with {ours}
            </caption>
            <thead>
              <tr>
                <th
                  scope="col"
                  className="w-[34%] pb-4 text-[13px] font-semibold tracking-[0.14em] text-[#6b6b6b] uppercase"
                >
                  <span className="sr-only">Capability</span>
                </th>
                <th
                  scope="col"
                  className="w-[33%] pb-4 pr-6 text-[15px] font-semibold tracking-tight text-[#6b6b6b]"
                >
                  {incumbent}
                </th>
                <th
                  scope="col"
                  className="font-rubik text-ink w-[33%] rounded-t-2xl bg-light-gray px-6 pt-5 pb-4 text-[17px] font-semibold tracking-tight"
                >
                  {ours}
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row, i) => (
                <tr key={row.label} className="border-t border-black/10">
                  <th
                    scope="row"
                    className="text-ink py-5 pr-6 align-top text-[15px] leading-6 font-semibold"
                  >
                    {row.label}
                  </th>
                  <td className="py-5 pr-6 align-top text-[15px] leading-6 text-[#6b6b6b]">{row.a}</td>
                  <td
                    className={`bg-light-gray px-6 py-5 align-top text-[15px] leading-6 text-[#2f2f2f] ${
                      i === rows.length - 1 ? 'rounded-b-2xl' : ''
                    }`}
                  >
                    <span className="flex items-start gap-2.5">
                      <span className="bg-primary/10 text-primary mt-0.5 flex size-5 shrink-0 items-center justify-center rounded-full">
                        <IconComponent icon="CheckMark" size="xsmall" className="size-3" />
                      </span>
                      {row.b}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </motion.div>

        {footnote && <p className="mt-6 max-w-[70ch] text-[14px] leading-6 text-[#6b6b6b]">{footnote}</p>}

        {buttons && buttons.length > 0 && (
          <div className="mt-10 flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-6">
            {buttons.map((b) =>
              b.variant === 'Contained' ? (
                <a
                  key={b.text}
                  href={b.href}
                  target={b.target}
                  rel={b.target === '_blank' ? 'noopener noreferrer' : undefined}
                  className="bg-primary hover:bg-signal inline-flex items-center justify-center rounded-full px-7 py-3.5 text-[16px] font-semibold text-white shadow-[0_12px_40px_-10px_rgba(193,71,233,0.7)] transition"
                >
                  {b.text}
                </a>
              ) : (
                // An external Text-variant link (Calendly) has to be a real anchor, not a
                // Next Link, so the click handler in components/Analytics sees the href it
                // expects and emits cta_type: 'book_demo'.
                <TextLink key={b.text} button={b} />
              )
            )}
          </div>
        )}
      </div>
    </section>
  )
}

export default Comparison
